import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Paperclip, Plus, Square, X } from 'lucide-react';
import { useIsBelowLg } from '../pages/hooks/useMediaQuery';

const BIQ_UPLOAD_ACCEPT = '.xlsx,.docx,.pdf,.txt,.csv';

const FILE_BADGE_COLORS = {
  PDF: 'bg-[#E24B4A]',
  XLSX: 'bg-[#217346]',
  XLS: 'bg-[#217346]',
  CSV: 'bg-[#217346]',
  DOCX: 'bg-[#2B579A]',
  DOC: 'bg-[#2B579A]',
  TXT: 'bg-[#5D625F]',
};

function fileExtensionLabel(name) {
  const i = (name || '').lastIndexOf('.');
  if (i < 0 || i === name.length - 1) return 'FILE';
  return name.slice(i + 1).toUpperCase().slice(0, 4);
}

export function BiqAttachedFileChip({ name, onRemove, disabled, removeLabel, label }) {
  if (!name) return null;
  const ext = fileExtensionLabel(name);
  return (
    <span
      className="inline-flex max-w-full items-center gap-2 rounded-lg border border-[#E8EBE9] bg-white/80 px-2 py-1 dark:border-white/10 dark:bg-black/20"
      aria-label={label}
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded text-[8px] font-bold leading-none text-white ${FILE_BADGE_COLORS[ext] || 'bg-[#828382]'}`}
        aria-hidden="true"
      >
        {ext}
      </span>
      <span className="min-w-0 max-w-[180px] truncate text-xs font-medium text-[#282A29] dark:text-[#f0f5f2]">
        {name}
      </span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={removeLabel}
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[#828382] hover:bg-black/10 hover:text-[#282A29] disabled:opacity-40 dark:hover:bg-white/15 dark:hover:text-white"
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      ) : null}
    </span>
  );
}

/**
 * Shared BiQ composer used by Speak with BiQ and the floating popup.
 *
 * `compact` hides the Default / Deep Thinking labels and shows only the
 * chevron — the popup is too narrow for the full label.
 */
const BiqComposer = ({
  inputRef,
  inputValue,
  onInputChange,
  onKeyDown,
  onFocus,
  onSend,
  onStop,
  isStreaming,
  loading,
  deepThinking,
  onDeepThinkingChange,
  conversationActive = false,
  compact = false,
  placeholder: placeholderOverride,
  onPlusClick,
  disabled = false,
  maxGrow = 160,
  pendingFile = null,
  onPickFile,
  onClearFile,
  attachError = '',
}) => {
  const { t } = useTranslation();
  const isBelowLg = useIsBelowLg();
  const [isFocused, setIsFocused] = useState(false);
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const modeDropdownRef = useRef(null);
  const attachMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputDisabled = disabled || loading || isStreaming;
  const canSend = Boolean(inputValue.trim() || pendingFile);
  const plusOpensMenu = Boolean(onPickFile);

  const defaultPlaceholder = isBelowLg
    ? t('biq.composer.placeholderShort')
    : t('biq.composer.placeholderLong');
  const placeholder = isFocused
    ? ''
    : (placeholderOverride ?? defaultPlaceholder);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showModeDropdown &&
        modeDropdownRef.current &&
        !modeDropdownRef.current.contains(event.target)
      ) {
        setShowModeDropdown(false);
      }
      if (
        showAttachMenu &&
        attachMenuRef.current &&
        !attachMenuRef.current.contains(event.target)
      ) {
        setShowAttachMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModeDropdown, showAttachMenu]);

  useLayoutEffect(() => {
    const el = inputRef?.current;
    if (!el) return;
    const resize = () => {
      const computed = window.getComputedStyle(el);
      const min = Math.ceil(parseFloat(computed.lineHeight) || 24);
      el.style.height = 'auto';
      const hasValue = el.value.length > 0;
      const next = hasValue ? Math.max(min, Math.min(el.scrollHeight, maxGrow)) : min;
      el.style.height = `${next}px`;
      el.style.overflowY = hasValue && el.scrollHeight > maxGrow ? 'auto' : 'hidden';
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [inputValue, inputRef, maxGrow]);

  const modeLabel = deepThinking
    ? t('biq.composer.deepThinking')
    : t('biq.composer.default');

  return (
    <div>
    <div
      className={`relative flex border pr-3 sm:pr-4 ${
        conversationActive
          ? 'border-[color:var(--biq-input-chat-border)] bg-[var(--biq-input-chat-bg)] shadow-[var(--biq-input-chat-shadow)]'
          : 'border-[#E8EBE9] bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] dark:border-white/10 dark:bg-[#2a2a2a] dark:shadow-[0_8px_24px_rgba(0,0,0,0.35)]'
      } ${
        pendingFile
          ? 'flex-col rounded-[26px] py-1.5'
          : 'min-h-[52px] items-center rounded-full'
      }`}
    >
      {pendingFile && (
        <div className="flex items-center pl-[46px] pr-2 pt-1.5">
          <BiqAttachedFileChip
            name={pendingFile.name}
            onRemove={onClearFile}
            disabled={inputDisabled}
            removeLabel={t('biq.removeFile')}
            label={t('biq.attachedFile')}
          />
        </div>
      )}

      <div className="relative flex min-h-[52px] min-w-0 w-full items-center">
      {plusOpensMenu ? (
        <div
          ref={attachMenuRef}
          className="absolute left-[26px] top-1/2 z-20 -translate-x-1/2 -translate-y-1/2"
        >
          <button
            type="button"
            onClick={() => {
              if (inputDisabled) return;
              setShowModeDropdown(false);
              setShowAttachMenu((open) => !open);
            }}
            disabled={inputDisabled}
            aria-label={t('biq.addDocument')}
            aria-haspopup="menu"
            aria-expanded={showAttachMenu}
            className="flex h-[28px] w-[28px] items-center justify-center rounded-full text-[#828382] transition-colors hover:bg-[#F0F5F2] hover:text-[#282A29] disabled:pointer-events-none dark:hover:bg-white/10 dark:hover:text-white"
          >
            <Plus size={18} strokeWidth={2} />
          </button>
          {showAttachMenu && (
            <div
              role="menu"
              className={`absolute left-0 z-50 min-w-[220px] overflow-hidden rounded-xl border border-[#E8EBE9] bg-white py-1 shadow-lg dark:border-white/10 dark:bg-[#1d1d1d] ${
                conversationActive ? 'bottom-full mb-2' : 'top-full mt-2'
              }`}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowAttachMenu(false);
                  fileInputRef.current?.click();
                }}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-[#282A29] transition-colors hover:bg-[#F0F5F2] dark:text-white dark:hover:bg-white/10"
              >
                <Paperclip size={16} strokeWidth={2} className="shrink-0 text-[#828382] dark:text-white/60" />
                {t('biq.uploadFiles')}
              </button>
              {onPlusClick ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setShowAttachMenu(false);
                    onPlusClick();
                  }}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-[#282A29] transition-colors hover:bg-[#F0F5F2] dark:text-white dark:hover:bg-white/10"
                >
                  <Plus size={16} strokeWidth={2} className="shrink-0 text-[#828382] dark:text-white/60" />
                  {t('biq.composer.newChat')}
                </button>
              ) : null}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={BIQ_UPLOAD_ACCEPT}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) onPickFile?.(file);
            }}
          />
        </div>
      ) : onPlusClick ? (
        <button
          type="button"
          onClick={onPlusClick}
          disabled={inputDisabled}
          aria-label={t('biq.composer.newChat')}
          className="absolute left-[26px] top-1/2 z-10 flex h-[28px] w-[28px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-[#828382] transition-colors hover:bg-[#F0F5F2] hover:text-[#282A29] disabled:pointer-events-none dark:hover:bg-white/10 dark:hover:text-white"
        >
          <Plus size={18} strokeWidth={2} />
        </button>
      ) : (
        <span
          className="pointer-events-none absolute left-[26px] top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center text-[#828382]"
          aria-hidden="true"
        >
          <Plus size={18} strokeWidth={2} />
        </span>
      )}

      <textarea
        ref={inputRef}
        rows={1}
        value={inputValue}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        onFocus={(e) => {
          setIsFocused(true);
          onFocus?.(e);
        }}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        disabled={inputDisabled}
        aria-label={placeholderOverride || t('biq.placeholder')}
        enterKeyHint="send"
        className={`composer-scroll min-h-[52px] min-w-0 flex-1 resize-none bg-transparent py-[13px] pl-[46px] text-sm leading-[26px] text-[#282A29] placeholder:text-[#828382] focus:outline-none disabled:opacity-50 dark:text-white dark:placeholder:text-white/45 ${compact ? '' : 'sm:text-base'}`}
        style={{ maxHeight: `${maxGrow}px` }}
      />

      <div className="relative flex shrink-0 items-center" ref={modeDropdownRef}>
        <button
          type="button"
          onClick={() => setShowModeDropdown((v) => !v)}
          className={`flex items-center rounded-full transition-colors hover:bg-[#F0F5F2] dark:hover:bg-white/10 ${
            compact
              ? 'p-1.5'
              : 'gap-1 px-2 py-1.5 text-xs font-medium sm:px-3 sm:text-sm'
          } ${
            deepThinking
              ? 'text-[#12823F] dark:text-[#7EE6A4]'
              : 'text-[#444746] dark:text-[#f0f5f2]'
          }`}
          aria-haspopup="listbox"
          aria-expanded={showModeDropdown}
          aria-label={t('biq.composer.modeAria', { mode: modeLabel })}
        >
          {!compact && <span>{modeLabel}</span>}
          <ChevronDown
            size={compact ? 16 : 14}
            className={`transition-transform ${showModeDropdown ? 'rotate-180' : ''}`}
          />
        </button>

        {showModeDropdown && (
          <div
            role="listbox"
            className="absolute bottom-full right-0 z-50 mb-2 min-w-[148px] overflow-hidden rounded-lg border border-[#E8EBE9] bg-white shadow-lg dark:border-white/10 dark:bg-[#1d1d1d]"
          >
            <button
              type="button"
              role="option"
              aria-selected={!deepThinking}
              onClick={() => {
                onDeepThinkingChange(false);
                setShowModeDropdown(false);
              }}
              className={`block w-full px-3 py-2 text-left text-sm transition-colors ${
                !deepThinking
                  ? 'bg-[#E3F5EB] text-[#12823F]'
                  : 'text-[#282A29] hover:bg-[#F0F5F2] dark:text-white dark:hover:bg-white/10'
              }`}
            >
              {t('biq.composer.default')}
            </button>
            <button
              type="button"
              role="option"
              aria-selected={deepThinking}
              onClick={() => {
                onDeepThinkingChange(true);
                setShowModeDropdown(false);
              }}
              className={`block w-full px-3 py-2 text-left text-sm transition-colors ${
                deepThinking
                  ? 'bg-[#E3F5EB] text-[#12823F]'
                  : 'text-[#282A29] hover:bg-[#F0F5F2] dark:text-white dark:hover:bg-white/10'
              }`}
            >
              {t('biq.composer.deepThinking')}
            </button>
          </div>
        )}
      </div>

      <div className="ml-2 flex shrink-0 items-center sm:ml-3">
        {isStreaming ? (
          <button
            type="button"
            onClick={onStop}
            aria-label={t('biq.composer.stop')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500 transition-colors hover:bg-red-600 sm:h-10 sm:w-10"
          >
            <Square size={16} className="text-white sm:hidden" />
            <Square size={18} className="hidden text-white sm:block" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend || inputDisabled}
            aria-label={t('biq.popup.sendMessage')}
            className="flex h-8 w-8 shrink-0 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
          >
            <img
              src="/assets/icons/send-button.png"
              alt=""
              width={32}
              height={32}
              className="h-8 w-8"
            />
          </button>
        )}
      </div>
    </div>
    </div>
    {attachError ? (
      <p className="mt-1.5 pl-[14px] text-xs text-red-600 dark:text-red-400">{attachError}</p>
    ) : null}
    </div>
  );
};

export default BiqComposer;
